import {MessageCircleDashed as MessageCircle} from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { useSignMessage } from 'wagmi'
import ColonyOfficialStar from '@/components/ColonyOfficialStar'
import SafePostText from '@/components/SafePostText'
import { isPenguinPadAdmin } from '@/lib/colonyOfficial'
import { formatRelativeTime } from '@/lib/format'
import { createPostComment, deletePostComment, listPostComments, onPostCommentCreated, onPostCommentDeleted, type PostComment, type PostCommentKind } from '@/lib/postComments'
import { WalletAddressContext } from '@/lib/walletContext'
import { colonyDisplayName } from '@/lib/walletProfiles'

export default function PostComments({
  postKind,
  postId,
  accent = 'lime',
}: {
  postKind: PostCommentKind
  postId: string
  accent?: 'lime' | 'orange'
}) {
  const wallet = useContext(WalletAddressContext)
  const { signMessageAsync } = useSignMessage()
  const [comments, setComments] = useState<PostComment[]>([])
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const isAdmin = isPenguinPadAdmin(wallet)
  const accentText = accent === 'orange' ? 'text-orange-400' : 'text-lime-400'

  useEffect(() => {
    let active = true
    setLoading(true)
    listPostComments(postKind, postId).then((list) => {
      if (active)
        setComments(list)
    }).finally(() => {
      if (active)
        setLoading(false)
    })
    return () => { active = false }
  }, [postKind, postId])

  useEffect(() => {
    return onPostCommentCreated((comment) => {
      if (comment.postKind !== postKind || comment.postId !== postId)
        return
      setComments(prev => [...prev.filter(item => item.id !== comment.id), comment])
    })
  }, [postKind, postId])

  useEffect(() => {
    return onPostCommentDeleted((commentId) => {
      setComments(prev => prev.filter(item => item.id !== commentId))
    })
  }, [])

  async function handleSubmit() {
    if (!wallet) {
      setError('Connect your wallet to comment')
      return
    }
    if (!draft.trim())
      return
    setBusy(true)
    setError('')
    try {
      await createPostComment({
        postKind,
        postId,
        wallet,
        content: draft,
        signMessage: message => signMessageAsync({ message }),
      })
      setDraft('')
    }
    catch (err) {
      setError(err instanceof Error ? err.message : 'Could not publish this comment')
    }
    finally {
      setBusy(false)
    }
  }

  async function handleDelete(commentId: string) {
    if (!wallet)
      return
    setBusy(true)
    setError('')
    try {
      await deletePostComment({
        commentId,
        wallet,
        signMessage: message => signMessageAsync({ message }),
      })
    }
    catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete this comment')
    }
    finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-3 border-t border-white/10 pt-3">
      <div className="flex items-center gap-1.5">
        <MessageCircle className={`h-3.5 w-3.5 ${accentText}`} aria-hidden="true" />
        <p className="text-xs font-semibold text-zinc-300">
          Comments
          {comments.length > 0 ? ` (${comments.length})` : ''}
        </p>
      </div>

      {loading
        ? <p className="mt-2 text-xs text-zinc-500">Loading comments…</p>
        : comments.length > 0
          ? (
              <ul className="mt-2 space-y-2">
                {comments.map(comment => (
                  <li key={comment.id} className="flex items-start justify-between gap-2 rounded-xl bg-white/[0.02] px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1 text-[11px] font-medium text-white">
                        {colonyDisplayName(comment.wallet)}
                        <ColonyOfficialStar wallet={comment.wallet} size="sm" />
                        <span className="font-normal text-zinc-500">
                          ·
                          {' '}
                          {formatRelativeTime(comment.createdAt)}
                        </span>
                      </p>
                      <SafePostText text={comment.content} className="mt-0.5 text-xs" />
                    </div>
                    {isAdmin
                      ? (
                          <button
                            type="button"
                            onClick={() => handleDelete(comment.id)}
                            disabled={busy}
                            className="shrink-0 text-[10px] font-semibold text-red-400 hover:text-red-300 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        )
                      : null}
                  </li>
                ))}
              </ul>
            )
          : <p className="mt-2 text-xs text-zinc-500">No comments yet.</p>}

      {wallet
        ? (
            <div className="mt-3 flex items-start gap-2">
              <input
                type="text"
                value={draft}
                onChange={event => setDraft(event.target.value)}
                maxLength={280}
                placeholder="Write a comment…"
                className="min-w-0 flex-1 rounded-full border border-white/10 bg-black/30 px-3 py-1.5 text-xs text-white placeholder:text-zinc-500 focus:border-lime-400/40 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={busy || !draft.trim()}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold text-black disabled:opacity-50 ${accent === 'orange' ? 'bg-orange-400' : 'bg-lime-400'}`}
              >
                Post
              </button>
            </div>
          )
        : <p className="mt-3 text-[11px] text-zinc-500">Connect your wallet to comment.</p>}
      {error ? <p className="mt-2 text-[11px] text-red-400">{error}</p> : null}
    </div>
  )
}
